import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Box, useScrollTrigger, IconButton, Drawer, List, ListItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Link as ScrollLink } from 'react-scroll';

const appBarStyles = {
  color: '#333',
  backgroundColor: '#f8f9fa',
  boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
};

const AvsAppBar = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  useEffect(() => {
    setScrolled(trigger);
  }, [trigger]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const resumeUrl = 'https://drive.google.com/uc?export=download&id=1fSsarOJvNW9Ecn1Na3EfFuEQS2vUJ-ve';

  const navItems = [
    { name: 'Home', to: 'intro' },
    { name: 'About', to: 'about' },
    { name: 'Education', to: 'education' },
    { name: 'Experience', to: 'experience' },
    { name: 'Skills', to: 'skills' },
    { name: 'Projects', to: 'projects' },
    { name: 'Agentic AI', to: 'agentic-ai' },
    { name: 'Blogs', to: 'blogs' },
    { name: 'Contact', to: 'contact' },
  ];

  return (
    <AppBar 
      position="fixed" 
      style={{
        ...appBarStyles,
        backgroundColor: scrolled ? 'rgba(248, 249, 250, 0.98)' : '#f8f9fa',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar>
        {isHome ? (
          <ScrollLink
            to="intro"
            spy={true}
            smooth={true}
            offset={-70}
            duration={500}
            style={{
              textDecoration: 'none',
              color: '#345995',
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                color: '#345995',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textAlign: 'left',
              }}
            >
              Venkat Sujit Adabala
            </Typography>
          </ScrollLink>
        ) : (
          <Typography
            component={RouterLink}
            to="/"
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: '#345995',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              textAlign: 'left',
              textDecoration: 'none',
            }}
          >
            Venkat Sujit Adabala
          </Typography>
        )}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          {navItems.map((item) =>
            isHome ? (
              <ScrollLink
                key={item.to}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-70}
                duration={500}
                style={{ textDecoration: 'none', color: '#333' }}
              >
                <Button
                  color="inherit"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#495057',
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: '#345995',
                      backgroundColor: 'rgba(52, 89, 149, 0.05)',
                    },
                  }}
                >
                  {item.name}
                </Button>
              </ScrollLink>
            ) : (
              <Button
                key={item.to}
                component={RouterLink}
                to={`/#${item.to}`}
                color="inherit"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  color: '#495057',
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: '#345995',
                    backgroundColor: 'rgba(52, 89, 149, 0.05)',
                  },
                }}
              >
                {item.name}
              </Button>
            )
          )}
          <Button
            component="a"
            href={resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            startIcon={<GetAppIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#345995',
              '&:hover': { backgroundColor: '#2a4777' },
            }}
          >
            Download Resume
          </Button>
        </Box>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { md: 'none' }, color: '#495057' }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: '#f8f9fa',
            paddingTop: '20px'
          },
        }}
      >
        <List>
          {navItems.map((item) => (
            <ListItem key={item.to} disablePadding>
              {isHome ? (
                <ScrollLink
                  to={item.to}
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={500}
                  onClick={handleDrawerToggle}
                  style={{
                    textDecoration: 'none',
                    color: '#495057',
                    width: '100%',
                    padding: '12px 24px',
                  }}
                >
                  <Button
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      color: '#495057',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        color: '#345995',
                        backgroundColor: 'rgba(52, 89, 149, 0.05)',
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                </ScrollLink>
              ) : (
                <Button
                  component={RouterLink}
                  to={`/#${item.to}`}
                  fullWidth
                  onClick={handleDrawerToggle}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#495057',
                    justifyContent: 'flex-start',
                    padding: '12px 24px',
                    '&:hover': {
                      color: '#345995',
                      backgroundColor: 'rgba(52, 89, 149, 0.05)',
                    },
                  }}
                >
                  {item.name}
                </Button>
              )}
            </ListItem>
          ))}
          <ListItem sx={{ pt: 2, px: 2 }}>
            <Button
              component="a"
              href={resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              fullWidth
              startIcon={<GetAppIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#345995',
                '&:hover': { backgroundColor: '#2a4777' },
              }}
            >
              Download Resume
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </AppBar>
  );
};

export default AvsAppBar;
